import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Wrench, Play, CheckCircle2, Package, Clock, Search,
  ChevronLeft, ChevronRight, AlertTriangle, RotateCcw
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { getCurrentRole, getCurrentUser } from '../utils/sessionManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  PENDING: 'bg-amber-100 text-amber-800',
  PLANNED: 'bg-blue-100 text-blue-800',
  RUNNING: 'bg-indigo-100 text-indigo-800',
  COMPLETED: 'bg-green-100 text-green-800'
};

const ReworkProduction = () => {
  const [wos, setWos] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Completion dialog
  const [completingWO, setCompletingWO] = useState(null);
  const [completionData, setCompletionData] = useState({ good_qty: 0, scrap_qty: 0, warehouse_id: '' });
  const [checkingCompletion, setCheckingCompletion] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    fetchReworkWOs();
  }, [page, statusFilter]);

  const fetchMasterData = async () => {
    try {
      const [itemRes, whRes] = await Promise.all([
        axios.get(`${API}/items`),
        axios.get(`${API}/warehouses`)
      ]);
      setItems(itemRes.data);
      setWarehouses(whRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchReworkWOs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: 'created_ts',
        sort_order: 'desc',
        wo_type: 'REWORK'
      });
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      const res = await axios.get(`${API}/production/orders?${params.toString()}`);
      if (res.data.data) {
        setWos(res.data.data);
        setTotalPages(res.data.pagination.total_pages);
        setTotalCount(res.data.pagination.total_count);
      } else {
        setWos(res.data);
      }
    } catch (error) {
      console.error('Error fetching rework WOs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getItemInfo = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? `${item.part_number} - ${item.description}` : itemId;
  };

  const getItemPartNumber = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item?.part_number || itemId;
  };

  const getUserRole = () => {
    try {
      const role = getCurrentRole();
      if (role?.name) return role.name;
      const user = getCurrentUser();
      if (user?.role_name) return user.role_name;
    } catch { /* ignore */ }
    return '';
  };

  const filteredWos = useMemo(() => {
    if (!searchQuery.trim()) return wos;
    const q = searchQuery.toLowerCase();
    return wos.filter(wo =>
      wo.wo_number?.toLowerCase().includes(q) ||
      wo.notes?.toLowerCase().includes(q) ||
      getItemPartNumber(wo.item_id).toLowerCase().includes(q)
    );
  }, [wos, searchQuery, items]);

  const scorecards = useMemo(() => ({
    total: totalCount,
    pending: wos.filter(w => w.status === 'PENDING' || w.status === 'PLANNED').length,
    running: wos.filter(w => w.status === 'RUNNING').length,
    completed: wos.filter(w => w.status === 'COMPLETED').length
  }), [wos, totalCount]);

  const handleStartProduction = async (woId) => {
    try {
      const userRole = getUserRole();
      await axios.post(`${API}/production/start/${woId}?user_role=${encodeURIComponent(userRole)}`);
      toast.success('Rework production started');
      fetchReworkWOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start rework production', { duration: 8000 });
    }
  };

  const openCompleteDialog = async (wo) => {
    setCheckingCompletion(true);
    try {
      const res = await axios.get(`${API}/production/can-complete/${wo.id}`);
      if (!res.data.canComplete) {
        toast.error(res.data.reason, { duration: 8000 });
        return;
      }
      setCompletingWO(wo);
      setCompletionData({
        good_qty: wo.plan_qty,
        scrap_qty: 0,
        warehouse_id: warehouses.length > 0 ? warehouses[0].id : ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check completion status');
    } finally {
      setCheckingCompletion(false);
    }
  };

  const handleCompleteProduction = async () => {
    if (!completingWO) return;
    const total = completionData.good_qty + completionData.scrap_qty;
    if (total !== completingWO.plan_qty) {
      toast.error(`Good + Scrap must equal plan qty (${completingWO.plan_qty})`);
      return;
    }
    if (!completionData.warehouse_id) {
      toast.error('Select a warehouse');
      return;
    }
    try {
      await axios.post(`${API}/production/complete/${completingWO.id}`, completionData);
      toast.success(`Rework completed! ${completionData.good_qty} good units added to inventory.`);
      setCompletingWO(null);
      fetchReworkWOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete rework', { duration: 8000 });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-6 rounded-lg border">
        <div className="flex items-center gap-3">
          <Wrench className="w-6 h-6 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Rework Production</h2>
            <p className="text-slate-600 text-sm">Manage rework work orders from customer returns</p>
          </div>
        </div>
      </div>

      {/* Scorecards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Rework WOs', value: scorecards.total, icon: Package, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Pending/Planned', value: scorecards.pending, icon: Clock, color: 'text-amber-700', bg: 'bg-amber-50' },
          { label: 'Running', value: scorecards.running, icon: Play, color: 'text-indigo-700', bg: 'bg-indigo-50' },
          { label: 'Completed', value: scorecards.completed, icon: CheckCircle2, color: 'text-green-700', bg: 'bg-green-50' },
        ].map(card => (
          <Card key={card.label} className="border shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${card.bg}`}><card.icon className={`w-5 h-5 ${card.color}`} /></div>
              <div><p className="text-2xl font-bold">{card.value}</p><p className="text-xs text-slate-500">{card.label}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input placeholder="Search WO #, item, notes..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-rework" />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="filter-rework-status"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="PLANNED">Planned</SelectItem>
            <SelectItem value="RUNNING">Running</SelectItem>
            <SelectItem value="COMPLETED">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Rework WO Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">WO #</th>
                  <th className="px-4 py-3 text-left font-semibold">Item</th>
                  <th className="px-4 py-3 text-left font-semibold">Return Ref</th>
                  <th className="px-4 py-3 text-center font-semibold">Plan Qty</th>
                  <th className="px-4 py-3 text-center font-semibold">Good Qty</th>
                  <th className="px-4 py-3 text-center font-semibold">Scrap Qty</th>
                  <th className="px-4 py-3 text-left font-semibold">Date</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWos.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                    {loading ? 'Loading...' : 'No rework work orders found'}
                  </td></tr>
                ) : filteredWos.map(wo => (
                  <tr key={wo.id} className="border-b hover:bg-slate-50" data-testid={`rework-wo-row-${wo.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-blue-700">{wo.wo_number}</span>
                        <Badge className="bg-orange-100 text-orange-700 text-[10px] border border-orange-200">REWORK</Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{getItemPartNumber(wo.item_id)}</td>
                    <td className="px-4 py-3">
                      {wo.notes ? (
                        <span className="text-xs text-slate-600">{wo.notes.replace('Rework WO created from ', '')}</span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-center font-semibold">{wo.plan_qty}</td>
                    <td className="px-4 py-3 text-center">
                      {wo.status === 'COMPLETED' ? <span className="text-green-700 font-semibold">{wo.good_qty || 0}</span> : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {wo.status === 'COMPLETED' ? <span className="text-red-600 font-semibold">{wo.scrap_qty || 0}</span> : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">{wo.prod_date || wo.start_date || '-'}</td>
                    <td className="px-4 py-3">
                      <Badge className={statusColors[wo.status] || 'bg-slate-100'}>{wo.status}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {(wo.status === 'PENDING' || wo.status === 'PLANNED') && (
                          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={() => handleStartProduction(wo.id)}
                            data-testid={`start-rework-${wo.id}`}>
                            <Play className="w-3.5 h-3.5 mr-1" /> Start
                          </Button>
                        )}
                        {wo.status === 'RUNNING' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => openCompleteDialog(wo)}
                            disabled={checkingCompletion}
                            data-testid={`complete-rework-${wo.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Complete
                          </Button>
                        )}
                        {wo.status === 'COMPLETED' && (
                          <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" /> Done</Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <span className="text-sm text-slate-500">Page {page} of {totalPages} ({totalCount} total)</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== COMPLETE DIALOG ===== */}
      <Dialog open={!!completingWO} onOpenChange={open => !open && setCompletingWO(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Complete Rework - {completingWO?.wo_number}
            </DialogTitle>
            <DialogDescription>
              Item: {completingWO && getItemInfo(completingWO.item_id)} | Plan Qty: {completingWO?.plan_qty}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Good Qty *</Label>
                <Input type="number" min="0" max={completingWO?.plan_qty}
                  value={completionData.good_qty}
                  onChange={e => setCompletionData(prev => ({ ...prev, good_qty: parseInt(e.target.value) || 0 }))}
                  className="border-green-300"
                  data-testid="rework-good-qty" />
              </div>
              <div>
                <Label>Scrap Qty</Label>
                <Input type="number" min="0" max={completingWO?.plan_qty}
                  value={completionData.scrap_qty}
                  onChange={e => setCompletionData(prev => ({ ...prev, scrap_qty: parseInt(e.target.value) || 0 }))}
                  className="border-red-300"
                  data-testid="rework-scrap-qty" />
              </div>
            </div>
            {completingWO && (completionData.good_qty + completionData.scrap_qty) !== completingWO.plan_qty && (
              <p className="text-xs text-red-600">Good + Scrap must equal plan qty ({completingWO.plan_qty})</p>
            )}
            <div>
              <Label>Warehouse *</Label>
              <Select value={completionData.warehouse_id} onValueChange={v => setCompletionData(prev => ({ ...prev, warehouse_id: v }))}>
                <SelectTrigger data-testid="rework-warehouse"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map(wh => <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompletingWO(null)}>Cancel</Button>
            <Button onClick={handleCompleteProduction} className="bg-green-600 hover:bg-green-700"
              disabled={!completingWO || (completionData.good_qty + completionData.scrap_qty) !== completingWO?.plan_qty || !completionData.warehouse_id}
              data-testid="confirm-complete-rework">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Complete Rework
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReworkProduction;
